import React from 'react';
import { useTranslation } from 'react-i18next';
import { subDays } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { formatPeriodEnds, useDateFnsLocale } from '@/lib/format-dates';
import { toReportingDateParam } from '../lib/reporting-url-state';

interface ReportingPeriodBarProps {
  preset: string;
  onPresetChange: (v: string) => void;
  range: DateRange | undefined;
  onRangeChange: (r: DateRange | undefined) => void;
}

/**
 * Date-range control for the Overview and Performance tabs (period-based). Stock
 * Value does NOT use this — it has its own as-of + waterfall-range controls.
 * Location is inherited from the top-nav location switcher, so it isn't here.
 */
export const ReportingPeriodBar: React.FC<ReportingPeriodBarProps> = ({
  preset,
  onPresetChange,
  range,
  onRangeChange,
}) => {
  const { t } = useTranslation('inventory');
  const locale = useDateFnsLocale();

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border bg-card p-4">
      <span className="text-sm font-medium text-muted-foreground">
        {t('reporting.filters.timePeriod', 'Time period')}
      </span>
      <Select value={preset} onValueChange={onPresetChange}>
        <SelectTrigger className="h-9 w-[150px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7">{t('reporting.filters.last7', 'Last 7 days')}</SelectItem>
          <SelectItem value="30">{t('reporting.filters.last30', 'Last 30 days')}</SelectItem>
          <SelectItem value="90">{t('reporting.filters.last90', 'Last 90 days')}</SelectItem>
          <SelectItem value="180">{t('reporting.filters.last180', 'Last 180 days')}</SelectItem>
          <SelectItem value="custom">{t('reporting.filters.custom', 'Custom')}</SelectItem>
        </SelectContent>
      </Select>

      {preset === 'custom' && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn('h-9 gap-2 font-normal', !range?.from && 'text-muted-foreground')}
            >
              <CalendarIcon className="h-4 w-4" aria-hidden="true" />
              {range?.from && range?.to
                ? t(
                    'reporting.threePlUsage.range',
                    formatPeriodEnds(toReportingDateParam(range.from), toReportingDateParam(range.to), locale),
                  )
                : t('reporting.filters.pickRange', 'Pick a range')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={range}
              defaultMonth={range?.from ?? subDays(new Date(), 30)}
              onSelect={onRangeChange}
              numberOfMonths={2}
              disabled={(date) => date > new Date() || date < new Date('2020-01-01')}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
